import { z } from 'zod';

/**
 * Contratos del flujo de pairing móvil↔PC (ADR-0005).
 *
 * Flujo resumido:
 *   1) PC POST /v1/sessions               → PairSessionResponse (roomCode + qrPayload)
 *   2) Móvil POST /v1/pair/claim          → ConfirmCode + pairSessionToken
 *   3) Móvil POST /v1/pair/approve        → "ok" tras doble confirmación visual
 *   4) Ambos abren WSS al LobbyRoom presentando pairSessionToken.
 */

/** Alfabeto del room code: A-Z menos I, O, Q (ADR-0005 §3.2). */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPRSTUVWXYZ' as const;

/** Longitud del room code: 4 letras → 23^4 ≈ 280k combinaciones. */
export const ROOM_CODE_LENGTH = 4;

/** Regex de validación del room code (mayúsculas, alfabeto restringido, longitud 4). */
export const ROOM_CODE_REGEX = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);

/** TTL del pair token (ADR-0005 §5: 60 s). */
export const PAIR_TOKEN_TTL_MS = 60_000;

/** Ventana de refresh del QR (ADR-0005 §6: cada 30 s). */
export const PAIR_TOKEN_REFRESH_MS = 30_000;

/** Confirm code visual (4 dígitos numéricos) que ambos lados comparan. */
export const CONFIRM_CODE_LENGTH = 4;
export const CONFIRM_CODE_REGEX = /^\d{4}$/;

// ---------- POST /v1/sessions ----------

export const PairSessionRequestSchema = z
  .object({
    /**
     * Pista opcional del cliente PC (UA, idioma). Solo informativa,
     * el servidor nunca confía en este valor para autoridad.
     */
    clientHint: z
      .object({
        userAgent: z.string().max(256).optional(),
        locale: z.string().max(16).optional(),
      })
      .optional(),
  })
  .strict();

export type PairSessionRequest = z.infer<typeof PairSessionRequestSchema>;

export const PairSessionResponseSchema = z
  .object({
    sessionId: z.string().uuid(),
    roomCode: z.string().regex(ROOM_CODE_REGEX),
    /** URL HTTPS al dominio oficial (legible) para fallback manual. */
    pairUrl: z.string().url(),
    /** Payload codificado en el QR (típicamente igual a pairUrl). */
    qrPayload: z.string().url(),
    /** Epoch ms en el que el token actual deja de ser válido. */
    expiresAt: z.number().int().positive(),
    /** Ventana de refresh sugerida al cliente (ms). */
    refreshAfterMs: z.number().int().positive(),
    /**
     * Token efímero que el PC anfitrión presenta a Game/LobbyRoom como rol "pc".
     * NO es un pair token (no requiere approve del Juramentario); identifica al
     * navegador anfitrión bound a esta sesión. TTL 2h (alineado con la sesión).
     */
    browserSessionToken: z.string().min(16),
  })
  .strict();

export type PairSessionResponse = z.infer<typeof PairSessionResponseSchema>;

// ---------- Pair token interno (HMAC) ----------

/**
 * Payload firmado dentro del pair token (HMAC server-bound, ADR-0005 §3).
 * Formato del token compactado: `base64url(payload).base64url(hmac)`.
 */
export const PairTokenSchema = z
  .object({
    /** JWT ID — single-use, blacklisted en Redis tras canjear. */
    jti: z.string().uuid(),
    /** Exp en epoch ms (no segundos). */
    exp: z.number().int().positive(),
    sessionId: z.string().uuid(),
    /** Identifica al navegador anfitrión que creó la sala. */
    browserSessionId: z.string().uuid(),
    /** Nonce que también aparece en la URL legible del QR. */
    nonce: z.string().min(8).max(64),
  })
  .strict();

export type PairToken = z.infer<typeof PairTokenSchema>;

// ---------- POST /v1/pair/claim ----------

export const DeviceFingerprintSchema = z
  .object({
    /** Hash estable derivado de UA + idioma + screen, NO PII. */
    hash: z.string().min(8).max(128),
    locale: z.string().max(16).optional(),
  })
  .strict();

export type DeviceFingerprint = z.infer<typeof DeviceFingerprintSchema>;

export const PairClaimRequestSchema = z
  .object({
    pairCode: z.string().regex(ROOM_CODE_REGEX),
    nonce: z.string().min(8).max(64),
    /** HMAC presente en la URL del QR; el servidor lo recomputa y compara. */
    hmac: z.string().min(16).max(256),
    deviceFingerprint: DeviceFingerprintSchema,
  })
  .strict();

export type PairClaimRequest = z.infer<typeof PairClaimRequestSchema>;

export const PairClaimResponseSchema = z
  .object({
    /** Token efímero que el móvil presenta a `/v1/pair/approve` y al WSS. */
    pairSessionToken: z.string().min(16),
    /** Código de 4 dígitos que también muestra la pantalla del PC. */
    confirmCode: z.string().regex(CONFIRM_CODE_REGEX),
    /** Alias generado tipo "Héroe 02" (ADR-0005 §49). */
    alias: z.string().min(1).max(32),
    /** Identificador estable del asiento de jugador. */
    seatId: z.string().uuid(),
  })
  .strict();

export type PairClaimResponse = z.infer<typeof PairClaimResponseSchema>;

// ---------- POST /v1/pair/approve ----------

export const PairConfirmRequestSchema = z
  .object({
    pairSessionToken: z.string().min(16),
    confirmCode: z.string().regex(CONFIRM_CODE_REGEX),
  })
  .strict();

export type PairConfirmRequest = z.infer<typeof PairConfirmRequestSchema>;

export const PairConfirmResponseSchema = z
  .object({
    ok: z.literal(true),
    seatId: z.string().uuid(),
    alias: z.string(),
  })
  .strict();

export type PairConfirmResponse = z.infer<typeof PairConfirmResponseSchema>;
